import React, { useState } from "react";
import { Box, Typography, Collapse } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ApprovalPipeline from "./ApprovalPipeline";
import { FTEBar, ParentRequestBlock, HistoryPanel } from "./RequestCardPanels";
import {
  STATUS_CONFIG, LEFT_BORDER, PARENT_STATUS_MAP,
  getInitials, getAvatarColor, formatDateRange,
} from "./RequestCardUtils";

const RequestCard = ({ request, onResubmit }) => {
  const theme   = useTheme();
  const t       = theme.custom;
  const isDark  = theme.palette.mode === "dark";
  const [expanded, setExpanded] = useState(false);

  const statusCfg   = STATUS_CONFIG[request.status] || STATUS_CONFIG.AT_PMO;
  const leftColor   = LEFT_BORDER[request.status]   || "#28B3B4";
  const avatarColor = getAvatarColor(request.associate?.fullName);
  const isRejected  = request.status === "REJECTED";
  const isResubmit  = !!request.parentRequestId && request.parentRequestStatus === "REJECTED";
  const isMove      = request.rawStatus === "MOVE_PENDING";
  const isAmendment = !!request.parentRequestId &&
    (request.parentRequestStatus === "ACTIVE" || request.parentRequestStatus === "SUPERSEDED") &&
    !isMove;

  return (
    <Box sx={{
      background: t.surface,
      border: `1px solid ${t.border2}`,
      borderLeft: `3px solid ${leftColor}`,
      borderRadius: "6px",
      mb: "8px",
      overflow: "hidden",
      transition: "box-shadow 0.15s",
      "&:hover": {
        boxShadow: isDark
          ? "0 4px 20px rgba(0,0,0,0.3)"
          : "0 4px 16px rgba(3,81,77,0.08)",
      },
    }}>

      {/* Resubmission banner */}
      {isResubmit && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "5px 16px",
          background: isDark ? "rgba(214,223,39,0.06)" : "rgba(214,223,39,0.07)",
          borderBottom: "1px solid rgba(214,223,39,0.18)",
        }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "2px 8px", borderRadius: "2px",
            background: "rgba(214,223,39,0.15)",
            border: "1px solid rgba(214,223,39,0.35)",
          }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              color: "#6B7300", letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase",
            }}>
              ↺ Resubmitted
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
            Replaces
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.accent, opacity: 0.85 }}>
            {request.parentRequestCode}
          </Typography>
          {request.parentRequestStatus && (() => {
            const ps = PARENT_STATUS_MAP[request.parentRequestStatus] ?? { label: request.parentRequestStatus, color: t.text3 };
            return (
              <Box sx={{ padding: "1px 7px", borderRadius: "2px", background: `${ps.color}18`, border: `1px solid ${ps.color}44` }}>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: ps.color, letterSpacing: "0.06em" }}>
                  {ps.label}
                </Typography>
              </Box>
            );
          })()}
        </Box>
      )}

      {/* Amendment banner */}
      {isAmendment && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "5px 16px",
          background: isDark ? "rgba(40,179,180,0.06)" : "rgba(40,179,180,0.07)",
          borderBottom: "1px solid rgba(40,179,180,0.18)",
        }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "2px 8px", borderRadius: "2px",
            background: "rgba(40,179,180,0.15)",
            border: "1px solid rgba(40,179,180,0.35)",
          }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              color: "#1a7a7b", letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase",
            }}>
              ⟳ Amendment
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
            of
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.accent, opacity: 0.85 }}>
            {request.parentRequestCode}
          </Typography>
          {request.parentFte != null && request.parentFte !== request.fte && (
            <Box sx={{
              ml: "4px", padding: "1px 7px", borderRadius: "2px",
              background: "rgba(40,179,180,0.10)",
              border: "1px solid rgba(40,179,180,0.25)",
            }}>
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", color: "#28B3B4" }}>
                FTE {request.parentFte} → {request.fte}
              </Typography>
            </Box>
          )}
        </Box>
      )}

      {/* Move banner */}
      {isMove && (
        <Box sx={{
          display: "flex", alignItems: "center", gap: "8px",
          padding: "5px 16px",
          background: isDark ? "rgba(200,138,58,0.07)" : "rgba(200,138,58,0.08)",
          borderBottom: "1px solid rgba(200,138,58,0.22)",
        }}>
          <Box sx={{
            display: "inline-flex", alignItems: "center", gap: "4px",
            padding: "2px 8px", borderRadius: "2px",
            background: "rgba(200,138,58,0.15)",
            border: "1px solid rgba(200,138,58,0.35)",
          }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              color: "#c88a3a", letterSpacing: "0.1em", fontWeight: 700, textTransform: "uppercase",
            }}>
              ⇄ Move Pending
            </Typography>
          </Box>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
            from
          </Typography>
          <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.accent, opacity: 0.85 }}>
            {request.parentRequestCode}
          </Typography>
        </Box>
      )}

      <Box sx={{ padding: "14px 16px" }}>

        {/* Top row */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <Box sx={{
              width: "32px", height: "32px", borderRadius: "50%", flexShrink: 0,
              background: `${avatarColor}22`, border: `1px solid ${avatarColor}55`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontFamily: "'DM Mono', monospace", fontSize: "10px", fontWeight: 700, color: avatarColor,
            }}>
              {getInitials(request.associate?.fullName, request.associate?.initials)}
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "13px", fontWeight: 600, color: t.text }}>
                  {request.associate?.fullName}
                </Typography>
                <Box sx={{ padding: "1px 7px", borderRadius: "3px", background: t.gold2, border: `1px solid ${t.gold}44` }}>
                  <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", fontWeight: 600, color: t.gold }}>
                    {request.associate?.levelCode}
                  </Typography>
                </Box>
                <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent, opacity: 0.8 }}>
                  {request.requestCode}
                </Typography>
              </Box>
              <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text2, mt: "2px" }}>
                {request.position?.positionCode} &nbsp;·&nbsp;
                {request.position?.roleTitle} &nbsp;·&nbsp;
                {request.position?.sowName}{request.accountName ? ` — ${request.accountName}` : ""}
                {request.position?.macName}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "4px", flexShrink: 0, ml: "12px" }}>
            <Box sx={{ padding: "3px 10px", borderRadius: "3px", background: statusCfg.bg, border: `1px solid ${statusCfg.border}` }}>
              <Typography sx={{
                fontFamily: "'DM Mono', monospace", fontSize: "9px",
                fontWeight: 600, color: statusCfg.color, letterSpacing: "0.06em",
              }}>
                {statusCfg.label}
              </Typography>
            </Box>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3 }}>
              {isRejected ? `By PMO · ${request.submittedAgo}` : `Submitted ${request.submittedAgo}`}
            </Typography>
          </Box>
        </Box>

        {/* Pipeline */}
        <ApprovalPipeline approvalStages={request.approvalStages} />

        {/* Rejection reason */}
        {isRejected && request.rejectionReason && (
          <Box sx={{ mt: "8px", mb: "2px" }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "3px",
            }}>
              Rejection Reason
            </Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: "#c84040" }}>
              {request.rejectionReason}
            </Typography>
          </Box>
        )}

        {/* Parent request block */}
        {(isResubmit || isAmendment) && (
          <ParentRequestBlock
            parentRequestId={request.parentRequestId}
            parentRequestCode={request.parentRequestCode}
            parentRequestStatus={request.parentRequestStatus}
            t={t}
            isDark={isDark}
          />
        )}

        {/* Detail grid */}
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: "12px", mt: "10px", pt: "10px", borderTop: `1px solid ${t.border}` }}>
          <Box>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "3px" }}>SOW - Account</Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", fontWeight: 500, color: t.text }}>
              {request.sow || "—"}
            </Typography>
            {request.accountName && (
              <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "9px", color: t.text3, mt: "1px" }}>
                {request.accountName}
              </Typography>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "5px" }}>Allocation</Typography>
            <FTEBar fte={request.fte} t={t} />
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "3px" }}>Duration</Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2 }}>
              {formatDateRange(request.startDate, request.endDate)}
            </Typography>
          </Box>
          <Box>
            <Typography sx={{ fontFamily: "'DM Mono', monospace", fontSize: "8px", letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "4px" }}>Skills Deployed</Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "11px", color: t.text2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {request.skills?.join(", ") || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Notes */}
        {request.notes && (
          <Box sx={{ mt: "10px", padding: "8px 12px", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: "3px" }}>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace", fontSize: "8px",
              letterSpacing: "0.12em", textTransform: "uppercase", color: t.text3, mb: "3px",
            }}>
              Notes
            </Typography>
            <Typography sx={{ fontFamily: "'Inter', sans-serif", fontSize: "12px", color: t.text2, lineHeight: 1.5 }}>
              {request.notes}
            </Typography>
          </Box>
        )}

        {/* Actions */}
        <Box sx={{ display: "flex", alignItems: "center", gap: "8px", mt: "10px" }}>
          {isRejected && (
            <Box
              onClick={() => onResubmit?.(request)}
              sx={{
                display: "inline-flex", alignItems: "center", gap: "6px",
                padding: "5px 12px", borderRadius: "4px", cursor: "pointer",
                border: `1px solid ${t.border2}`, background: t.surface2,
                fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.text2,
                transition: "all 0.15s",
                "&:hover": { borderColor: t.accent, color: t.accent },
              }}
            >
              ↺ Resubmit
            </Box>
          )}
          <Box
            onClick={() => setExpanded((p) => !p)}
            sx={{
              display: "inline-flex", alignItems: "center", gap: "4px", cursor: "pointer",
              fontFamily: "'DM Mono', monospace", fontSize: "10px", color: t.accent,
              "&:hover": { opacity: 0.75 },
            }}
          >
            History {expanded ? "↑" : "→"}
          </Box>
        </Box>
      </Box>

      {/* History panel */}
      <Collapse in={expanded}>
        <HistoryPanel request={request} t={t} />
      </Collapse>
    </Box>
  );
};

export default RequestCard;